import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Sparkles,
  Crosshair,
  BookOpen,
  Wrench,
  Film,
  PenLine,
  User,
  CreditCard,
  DollarSign,
  Bell,
  Bookmark,
  ListChecks,
  Map,
  LogOut,
  Lock,
  ExternalLink,
  ChevronDown,
  Check,
  Menu,
  X,
  Star,
  HelpCircle,
} from "lucide-react";
import { logout as apiLogout } from "@/lib/api";
import { usePrice } from "@/lib/price";
import { getList } from "@/lib/storage";
import NotificationsBell from "@/components/dashboard/NotificationsBell";
import { restartTour } from "@/components/dashboard/DashboardTour";

const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

/**
 * V2/V1 dashboard switcher. Renders the title "DASHBOARD ▾" + a dropdown
 * with two entries. V2 = the new app you're already on, V1 = the legacy
 * dashboard at jobhacki.com (?v2=0 short-circuits to the classic build).
 */
function VersionSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  // `?v2=0` = user is intentionally on V1; anything else (incl. no flag) = V2.
  const onV1 = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("v2") === "0";
  const currentVersion = onV1 ? "V1" : "V2";

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="dash-version-switcher"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch dashboard version"
        className="hover-pop inline-flex items-center gap-2 px-2 py-1 -ml-2 border-2 border-transparent hover:border-[#0a0a0a] hover:bg-[#FFC400]"
      >
        <span className="font-display text-base md:text-lg truncate">DASHBOARD</span>
        <span
          className="font-accent text-[9px] bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-1.5 py-0.5 leading-none"
          data-testid="dash-version"
        >
          {currentVersion}
        </span>
        <ChevronDown size={14} strokeWidth={3} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="dash-version-menu"
          className="absolute left-0 top-full mt-2 z-30 min-w-[260px] bg-white border-4 border-[#0a0a0a] shadow-brutal"
        >
          <a
            href="/dashboard?v2=1"
            role="menuitem"
            data-testid="dash-version-v2"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-4 py-3 border-b-2 border-[#0a0a0a]/10 hover:bg-[#FFC400] transition-colors"
          >
            <span className="font-accent text-[10px] bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-1.5 py-0.5 leading-none mt-0.5">
              V2
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm flex items-center gap-2">
                Version 2 — New
                {currentVersion === "V2" && (
                  <span className="font-accent text-[9px] bg-[#E10600] text-white border-2 border-[#0a0a0a] px-1.5 py-0.5 leading-none">
                    V2 CURRENT
                  </span>
                )}
              </div>
              <div className="font-mono-body text-[11px] text-[#0a0a0a]/65 mt-0.5">
                Brutalist redesign · native tools · cross-device lists.
              </div>
            </div>
            {currentVersion === "V2" && <Check size={14} strokeWidth={3} className="mt-1 shrink-0" />}
          </a>
          <a
            href="https://jobhacki.com/dashboard?v2=0"
            role="menuitem"
            data-testid="dash-version-v1"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-4 py-3 hover:bg-[#FFC400] transition-colors"
          >
            <span className="font-accent text-[10px] bg-white text-[#0a0a0a] border-2 border-[#0a0a0a] px-1.5 py-0.5 leading-none mt-0.5">
              V1
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm flex items-center gap-2">
                Version 1 — Classic
                {currentVersion === "V1" && (
                  <span className="font-accent text-[9px] bg-[#E10600] text-white border-2 border-[#0a0a0a] px-1.5 py-0.5 leading-none">
                    V1 CURRENT
                  </span>
                )}
              </div>
              <div className="font-mono-body text-[11px] text-[#0a0a0a]/65 mt-0.5">
                The original jobhacki.com dashboard.
              </div>
            </div>
            {currentVersion === "V1"
              ? <Check size={14} strokeWidth={3} className="mt-1 shrink-0" />
              : <ExternalLink size={12} strokeWidth={3} className="mt-1 shrink-0 opacity-55" />}
          </a>
        </div>
      )}
    </div>
  );
}

const SECTIONS = [
  {
    title: "Dashboard",
    items: [
      { label: "Home", to: "/dashboard", icon: LayoutDashboard, end: true },
    ],
  },
  {
    title: "Tools",
    items: [
      { label: "Job Directory", to: "/dashboard/jobs", icon: Briefcase, tier: "free" },
      { label: "Resume Match", to: "/dashboard/match", icon: Sparkles, tier: "free", badge: "NEW" },
      { label: "Job Readiness", to: "/dashboard/readiness", icon: Crosshair, tier: "paid" },
      { label: "Resume Builder", to: "/dashboard/embed/resume", icon: FileText, tier: "paid" },
      { label: "Simulator", to: "/dashboard/embed/simulator", icon: Crosshair, tier: "paid" },
      { label: "RoleFit", to: "/dashboard/rolefit", icon: Star, tier: "free" },
      { label: "Career Path", to: "/dashboard/career-path", icon: Map, tier: "paid", badge: "NEW" },
      { label: "Playbooks", to: "/dashboard/playbooks", icon: BookOpen, tier: "free" },
      { label: "Toolbox", to: "/dashboard/toolbox", icon: Wrench, tier: "free" },
      { label: "OmniCut", to: "https://jobhacki.com/omnicut", icon: Film, tier: "paid", external: true },
      { label: "OmniScript", to: "https://jobhacki.com/omniscript", icon: PenLine, tier: "paid", external: true },
    ],
  },
  {
    title: "Tracking",
    items: [
      { label: "Saved Jobs", to: "/dashboard/saved", icon: Bookmark, tier: "free", listKind: "saved" },
      { label: "Job Alerts", to: "/dashboard/alerts", icon: Bell, tier: "free", listKind: "alerts" },
      { label: "Applications", to: "/dashboard/applications", icon: ListChecks, tier: "free", listKind: "apps" },
    ],
  },
  {
    title: "Account",
    items: [
      { label: "Profile", to: "/dashboard/profile", icon: User },
      { label: "Billing", to: "/dashboard/billing", icon: CreditCard },
      { label: "Refer & Earn", to: "/affiliate", icon: DollarSign, external: true },
    ],
  },
];

/**
 * Reactive list-size hook: returns the current localStorage list length and
 * re-renders whenever storage.js dispatches its `jh:storage` event.
 */
function useListCount(kind) {
  const [n, setN] = useState(() => (kind ? getList(kind).length : 0));
  useEffect(() => {
    if (!kind) return undefined;
    const refresh = () => setN(getList(kind).length);
    const onStorage = (e) => {
      if (!e?.detail?.key || e.detail.key.includes(kind)) refresh();
    };
    refresh();
    window.addEventListener("jh:storage", onStorage);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("jh:storage", onStorage);
      window.removeEventListener("storage", refresh);
    };
  }, [kind]);
  return n;
}

function NavItem({ item, paid, onClickClose }) {
  const Icon = item.icon;
  const locked = item.tier === "paid" && !paid;
  const count = useListCount(item.listKind);

  const inner = (
    <span className="flex items-center gap-3 w-full">
      <Icon size={16} strokeWidth={2.5} className="shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {count > 0 && (
        <span
          data-testid={`nav-count-${item.to.split("/").pop()}`}
          className="font-accent text-[10px] bg-[#E10600] text-white border border-[#0a0a0a] px-1.5 py-0.5 leading-none min-w-[20px] text-center"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
      {item.badge && (
        <span className="font-accent text-[9px] bg-[#FFC400] text-[#0a0a0a] border border-[#0a0a0a] px-1.5 py-0.5 leading-none">
          {item.badge}
        </span>
      )}
      {locked && <Lock size={12} strokeWidth={3} className="text-[#0a0a0a]/50" />}
      {item.external && <ExternalLink size={11} strokeWidth={3} className="text-[#0a0a0a]/40" />}
    </span>
  );

  const baseClass =
    "flex items-center px-3 py-2.5 font-accent text-[13px] border-2 border-transparent";
  const idleClass = "text-[#0a0a0a] hover:bg-[#FFC400] hover:border-[#0a0a0a]";
  const activeClass = "bg-[#0a0a0a] text-[#FFC400] border-[#0a0a0a]";

  if (item.external) {
    return (
      <a
        href={item.to}
        target={item.to.startsWith("http") ? "_blank" : undefined}
        rel={item.to.startsWith("http") ? "noopener noreferrer" : undefined}
        onClick={onClickClose}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`${baseClass} ${idleClass}`}
      >
        {inner}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClickClose}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={({ isActive }) =>
        `${baseClass} ${isActive ? activeClass : idleClass}`
      }
    >
      {inner}
    </NavLink>
  );
}

export default function Shell({ me, refresh, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  const paid = Boolean(me?.paid);
  const email = me?.user?.email || me?.email || "you";
  const { price, remaining, cap, checkout } = usePrice();
  const whop = checkout || WHOP;

  const doLogout = async () => {
    await apiLogout();
    await refresh();
    nav("/dashboard");
  };

  const SidebarContent = (
    <>
      <div className="px-4 py-5 border-b-4 border-[#0a0a0a] bg-[#FDFBF7]">
        <a
          href="/"
          data-testid="dash-logo"
          className="flex items-center gap-2 font-display text-xl tracking-tight"
        >
          <img
            src="/jobhacki-logo.png"
            alt="JobHacki"
            className="h-9 w-9 border-2 border-[#0a0a0a] rounded-md object-cover"
          />
          JOBHACKI
        </a>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {paid ? (
            <span
              data-testid="entitlement-badge"
              className="font-accent text-[10px] px-2 py-1 border-2 border-[#0a0a0a] bg-[#FFC400] text-[#0a0a0a] inline-flex items-center gap-1"
            >
              <Star size={10} strokeWidth={3} fill="#0a0a0a" /> ALL-ACCESS
            </span>
          ) : (
            <>
              <span
                data-testid="entitlement-badge"
                className="font-accent text-[10px] px-2 py-1 border-2 border-[#0a0a0a] bg-white text-[#0a0a0a]/70"
              >
                FREE
              </span>
              <a
                href={whop}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="entitlement-upgrade"
                className="font-accent text-[10px] px-2 py-1 border-2 border-[#0a0a0a] bg-[#E10600] text-white hover:bg-[#0a0a0a]"
              >
                Upgrade {price} →
              </a>
            </>
          )}
        </div>
        <div className="font-mono-body text-[10px] text-[#0a0a0a]/55 mt-2">
          {!paid && remaining && cap ? (
            <>
              <b className="text-[#E10600]">{remaining.toLocaleString()}</b> of {cap.toLocaleString()} launch spots left at <b>{price}</b>
            </>
          ) : (
            ""
          )}
        </div>
        <div className="font-mono-body text-[11px] text-[#0a0a0a]/60 mt-3 truncate">
          {email}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-5">
        {SECTIONS.map((sec) => (
          <div key={sec.title}>
            <div className="font-accent text-[10px] text-[#0a0a0a]/50 px-3 pb-1.5 tracking-widest">
              {sec.title.toUpperCase()}
            </div>
            <div className="flex flex-col">
              {sec.items.map((it) => (
                <NavItem
                  key={it.label}
                  item={it}
                  paid={paid}
                  onClickClose={() => setMobileOpen(false)}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t-4 border-[#0a0a0a] space-y-2">
        <button
          onClick={restartTour}
          data-testid="dash-restart-tour"
          className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-[11px] hover-pop"
        >
          <HelpCircle size={11} strokeWidth={3} /> Replay tour
        </button>
        <button
          onClick={doLogout}
          data-testid="dash-logout"
          className="hover-pop w-full inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-xs shadow-brutal-sm"
        >
          <LogOut size={13} strokeWidth={3} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0a0a0a] flex">
      <aside
        data-testid="dash-sidebar"
        className="hidden md:flex w-64 shrink-0 border-r-4 border-[#0a0a0a] bg-white flex-col fixed inset-y-0 left-0 z-30"
      >
        {SidebarContent}
      </aside>

      <div className={`md:hidden fixed inset-0 z-50 ${mobileOpen ? "" : "pointer-events-none"}`}>
        <div
          className={`absolute inset-0 bg-[#0a0a0a]/60 transition-opacity ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          data-testid="dash-sidebar-mobile"
          className={`absolute inset-y-0 left-0 w-72 bg-white border-r-4 border-[#0a0a0a] flex flex-col transition-transform ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {SidebarContent}
        </aside>
      </div>

      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-[#FDFBF7] border-b-4 border-[#0a0a0a] flex items-center gap-3 px-4 md:px-8 h-[64px]">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-10 h-10 border-2 border-[#0a0a0a] flex items-center justify-center bg-[#FFC400]"
            aria-label="Open menu"
            data-testid="dash-mobile-toggle"
          >
            <Menu size={18} strokeWidth={3} />
          </button>
          <VersionSwitcher />
          {me?.demo && (
            <span
              data-testid="demo-badge"
              className="font-accent text-[10px] bg-[#E10600] text-white border-2 border-[#0a0a0a] px-2 py-0.5"
              title="Preview-only demo mode — ?demo=free / ?demo=out to switch state"
            >
              DEMO
            </span>
          )}
          <div className="flex-1" />
          <NotificationsBell />
          {paid ? (
            <span
              data-testid="dash-topbar-pro"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#FFC400] text-[#0a0a0a] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-xs shadow-brutal-sm"
            >
              <Star size={11} strokeWidth={3} fill="#0a0a0a" /> PRO
            </span>
          ) : (
            <a
              href={whop}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dash-topbar-unlock"
              className="hover-pop hidden sm:inline-flex items-center gap-1.5 bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-xs shadow-brutal-sm"
            >
              <Lock size={12} strokeWidth={3} /> Unlock {price}
              <ExternalLink size={10} strokeWidth={3} />
            </a>
          )}
        </header>

        <main className="flex-1 px-4 md:px-8 py-6 md:py-10">{children}</main>

        <footer className="px-4 md:px-8 py-6 border-t-2 border-[#0a0a0a]/15 font-mono-body text-[11px] text-[#0a0a0a]/55 flex flex-wrap gap-x-4 gap-y-1">
          <span>© {new Date().getFullYear()} JobHacki</span>
          <a href="/privacy" className="hover:underline">Privacy</a>
          <a href="/terms" className="hover:underline">Terms</a>
          <a href="/refund" className="hover:underline">Refund</a>
          <a href="/disclaimer" className="hover:underline">Disclaimer</a>
          <span className="ml-auto">built loud · grounded · keyless</span>
        </footer>
      </div>
    </div>
  );
}
